import { useElementSize } from '@custom-react-hooks/use-element-size';
import { Box, Button, Stack, alpha, useTheme } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import JsonField from '../../jsonField';
import RadioGroup from '../../raidioGroup';
import TextField from '../../textField';

type Props = {
  questions: Record<string, any>;
  userResponses: Record<string, any>;
  maxHeight: number;
  onUserResponseChange: (questionId: string, response: any) => void;
  onBack: () => void;
  onCancel: () => void;
  onNext: () => void;
};

const StepExtractInfo = ({
  questions = {},
  userResponses = {},
  maxHeight,
  onUserResponseChange,
  onBack,
  onCancel,
  onNext,
}: Props) => {
  const getQuestionDef = (qId: string) => {
    if (!questions) return null;
    if (Array.isArray(questions)) {
      for (const item of questions) {
        if (item.id === qId) return item;
        if (item.questions && Array.isArray(item.questions)) {
          const found = item.questions.find((q: any) => q.id === qId);
          if (found) return found;
        }
      }
    } else if (typeof questions === 'object') {
      return questions[qId] || null;
    }
    return null;
  };

  const { t } = useTranslation();
  const theme = useTheme();

  const [containerRef, containerSize] = useElementSize();
  const [actionPanelRef, actionPanelSize] = useElementSize();

  const [errors, setErrors] = useState<any>({});

  const setExtractionType = (response: string) => {
    onUserResponseChange('extractionType', response);
  };

  const setEntityNames = (response: string) => {
    onUserResponseChange('entityNames', response);
  };

  const setEntityType = (response: string) => {
    onUserResponseChange('entityType', response);
  };

  const setAttributeNames = (response: string) => {
    onUserResponseChange('attributeNames', response);
  };

  const setEntityDescription = (response: string) => {
    onUserResponseChange('entityDescription', response);
  };

  const setJsonSchema = (response: string) => {
    onUserResponseChange('jsonSchema', response);
  };

  const setJsonSample = (response: string) => {
    onUserResponseChange('jsonSample', response);
  };

  const handleNext = () => {
    const errors: any = {};
    let hasError = false;
    if (!userResponses.extractionType) {
      errors.extractionType = t('common.errors.requiredField');
      hasError = true;
    }
    if (userResponses.extractionType === 'entityNames') {
      if (!userResponses.entityNames) {
        errors.entityNames = t('common.errors.requiredField');
        hasError = true;
      }
    }
    if (userResponses.extractionType === 'entityType') {
      if (!userResponses.entityType) {
        errors.entityType = t('common.errors.requiredField');
        hasError = true;
      }
    }

    if (userResponses.extractionType === 'entityDescription') {
      if (!userResponses.entityDescription) {
        errors.entityDescription = t('common.errors.requiredField');
        hasError = true;
      }
    }

    if (userResponses.extractionType === 'jsonSchema') {
      if (!userResponses.jsonSchema) {
        errors.jsonSchema = t('common.errors.requiredField');
        hasError = true;
      }
    }

    if (userResponses.extractionType === 'jsonSample') {
      if (!userResponses.jsonSample) {
        errors.jsonSample = t('common.errors.requiredField');
        hasError = true;
      }
    }

    setErrors(errors);
    if (!hasError) {
      onNext();
    }
  };

  const handleCancel = () => {
    onCancel();
  };

  const handleBack = () => {
    onBack();
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        height: `${maxHeight}px`,
        display: 'flex',
        flexDirection: 'column',
        border: 1,
        borderColor: 'divider',
        borderLeft: `3px solid ${theme.palette.secondary.main}`,
        borderRadius: '12px',
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
        <Stack alignItems={'flex-start'} spacing={4}>
          {userResponses.mainRequirements[0] === 'extractInfo' && (
            <RadioGroup
              label={(getQuestionDef('extractionType')?.questionText || getQuestionDef('extractionType')?.question || 'Extraction Type')}
              value={userResponses.extractionType || ''}
              options={(getQuestionDef('extractionType')?.options || [])}
              required
              error={errors.extractionType}
              onChange={setExtractionType}
              maxOptionsPerColumn={3}
            />
          )}
          {userResponses.mainRequirements[0] === 'extractInfo' && userResponses.extractionType === 'entityNames' && (
            <TextField
              label={(getQuestionDef('entityNames')?.questionText || getQuestionDef('entityNames')?.question || 'Entity Names')}
              value={userResponses.entityNames || ''}
              required
              error={errors.entityNames}
              fullWidth={false}
              onChange={setEntityNames}
            />
          )}
          {userResponses.mainRequirements[0] === 'extractInfo' && userResponses.extractionType === 'entityType' && (
            <TextField
              label={(getQuestionDef('entityType')?.questionText || getQuestionDef('entityType')?.question || 'Entity Type')}
              value={userResponses.entityType || ''}
              required
              error={errors.entityType}
              fullWidth={false}
              onChange={setEntityType}
            />
          )}
          {userResponses.mainRequirements[0] === 'extractInfo' &&
            (userResponses.extractionType === 'entityNames' || userResponses.extractionType === 'entityType') && (
              <TextField
                label={(getQuestionDef('attributeNames')?.questionText || getQuestionDef('attributeNames')?.question || 'Attribute Names')}
                value={userResponses.attributeNames || ''}
                error={errors.attributeNames}
                fullWidth={false}
                onChange={setAttributeNames}
              />
            )}
          {userResponses.mainRequirements[0] === 'extractInfo' &&
            userResponses.extractionType === 'entityDescription' && (
              <TextField
                label={(getQuestionDef('entityDescription')?.questionText || getQuestionDef('entityDescription')?.question || 'Entity Description')}
                value={userResponses.entityDescription || ''}
                required
                multiline
                rows={5}
                error={errors.entityDescription}
                onChange={setEntityDescription}
              />
            )}
          {userResponses.mainRequirements[0] === 'extractInfo' && userResponses.extractionType === 'jsonSchema' && (
            <JsonField
              label={(getQuestionDef('jsonSchema')?.questionText || getQuestionDef('jsonSchema')?.question || 'JSON Schema')}
              value={userResponses.jsonSchema || ''}
              required
              error={errors.jsonSchema}
              rows={5}
              onChange={setJsonSchema}
            />
          )}

          {userResponses.mainRequirements[0] === 'extractInfo' && userResponses.extractionType === 'jsonSample' && (
            <JsonField
              label={(getQuestionDef('jsonSample')?.questionText || getQuestionDef('jsonSample')?.question || 'JSON Sample')}
              value={userResponses.jsonSample || ''}
              required
              error={errors.jsonSample}
              rows={5}
              onChange={setJsonSample}
              isJsonSchema={false}
            />
          )}
        </Stack>
      </Box>
      <Box
        ref={actionPanelRef}
        sx={{ px: 3, py: 1.5, borderTop: 1, borderColor: 'divider', bgcolor: alpha(theme.palette.primary.main, 0.02) }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Button
            variant="text"
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
          >
            {t('common.buttons.back')}
          </Button>
          <Stack direction="row" spacing={1.5}>
            <Button
              variant="text"
              onClick={onCancel}
              sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
            >
              {t('common.buttons.cancel')}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              endIcon={<ArrowForwardIcon />}
              disabled={!userResponses.extractionType}
              onClick={handleNext}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: '10px',
                px: 2.5,
                color: 'text.primary',
                boxShadow: 'none',
                '&:hover': { boxShadow: 'none' },
              }}
            >
              {t('common.buttons.next')}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

export default StepExtractInfo;
